<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MdiChartBar from '~icons/mdi/chart-bar'
import MdiCalendar from '~icons/mdi/calendar'

const { t } = useI18n()
const readinessMeta = {
  available: { labelKey: 'instanceStatus.available', tone: 'ok' },
  partial: { labelKey: 'ui.partial', tone: 'warn' },
  unavailable: { labelKey: 'ui.unavailable', tone: 'bad' },
  low_stock: { labelKey: 'ui.lowStock', tone: 'warn' },
  maintenance: { labelKey: 'itemView.maintenanceBadge', tone: 'muted' },
  unknown: { labelKey: 'common.unknown', tone: 'muted' },
}

const props = defineProps({ title: String, kind: String, data: Object, error: String, loading: Boolean })
defineEmits(['retry'])

const isReadiness = computed(() => props.kind === 'readiness')
const HintIcon = computed(() => (isReadiness.value ? MdiChartBar : MdiCalendar))

const rows = computed(() => {
  if (isReadiness.value) {
    return Object.entries(props.data?.readiness ?? {}).map(([key, count]) => {
      const meta = readinessMeta[key] || { labelKey: null, tone: 'muted' }
      return {
        key,
        label: meta.labelKey ? t(meta.labelKey) : key.replaceAll('_', ' '),
        count: Number(count) || 0,
        tone: meta.tone,
      }
    })
  }
  return (props.data?.workload ?? []).map((row) => ({
    key: row.date,
    label: formatWorkloadLabel(row.date),
    shortLabel: formatWorkloadShort(row.date),
    count: Number(row.count) || 0,
    tone: 'accent',
  }))
})

const max = computed(() => Math.max(1, ...rows.value.map((row) => row.count)))
const total = computed(() => rows.value.reduce((sum, row) => sum + row.count, 0))

function formatWorkloadLabel(date) {
  if (!date) return ''
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  return parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function formatWorkloadShort(date) {
  if (!date) return ''
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  return parsed.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' })
}

function barWidth(count) {
  return { width: `${Math.max(6, Math.round((count / max.value) * 100))}%` }
}

function colHeight(count) {
  return { height: `${Math.max(8, Math.round((count / max.value) * 100))}%` }
}
</script>

<template>
  <div class="fp-chart">
    <header class="fp-chart__header">
      <div class="fp-chart__heading">
        <span class="fp-chart__icon" :class="isReadiness ? 'fp-accent--emerald' : 'fp-accent--indigo'">
          <component :is="HintIcon" class="w-4 h-4" />
        </span>
        <div class="min-w-0">
          <h3 class="fp-chart__title">{{ title }}</h3>
          <p class="fp-chart__hint">
            {{ isReadiness ? $t('ui.inventoryRecordsByAvailabilityStatus') : $t('ui.projectsHoldingEquipmentPerDay') }}
          </p>
        </div>
      </div>
      <div v-if="!loading && !error && rows.length" class="fp-chart__total tabular-nums">
        {{ total }}
      </div>
    </header>

    <p v-if="loading" class="fp-chart__state" role="status">{{ $t('ui.loadingChart') }}</p>
    <div v-else-if="error" class="fp-chart__state" role="alert">
      {{ error }}
      <button type="button" class="btn small" @click="$emit('retry')">{{ $t('common.retry') }}</button>
    </div>
    <p v-else-if="!rows.length" class="fp-chart__state">{{ $t('ui.noAccessibleData') }}</p>

    <!-- Readiness: modern horizontal status bars -->
    <ul v-else-if="isReadiness" class="fp-chart__rows" :aria-label="title">
      <li v-for="row in rows" :key="row.key" class="fp-chart__row">
        <div class="fp-chart__row-meta">
          <span class="fp-chart__label">{{ row.label }}</span>
          <span class="fp-chart__count tabular-nums">{{ row.count }}</span>
        </div>
        <div class="fp-chart__track" role="presentation">
          <div
            class="fp-chart__bar"
            :class="`fp-chart__bar--${row.tone}`"
            :style="barWidth(row.count)"
            role="meter"
            :aria-label="row.label"
            :aria-valuemin="0"
            :aria-valuemax="max"
            :aria-valuenow="row.count"
          />
        </div>
      </li>
    </ul>

    <!-- Workload: vertical column chart -->
    <ul v-else class="fp-chart__cols" :aria-label="title">
      <li v-for="row in rows" :key="row.key" class="fp-chart__col">
        <div class="fp-chart__col-plot">
          <span v-if="row.count" class="fp-chart__col-count tabular-nums">{{ row.count }}</span>
          <div
            class="fp-chart__col-bar"
            :style="colHeight(row.count)"
            role="meter"
            :aria-label="row.label"
            :aria-valuemin="0"
            :aria-valuemax="max"
            :aria-valuenow="row.count"
          />
        </div>
        <span class="fp-chart__col-label" :title="row.label">{{ row.shortLabel }}</span>
      </li>
    </ul>

    <div class="fp-chart__footer">
      <router-link class="btn small" :to="isReadiness ? '/items' : '/projects'">
        {{ isReadiness ? $t('ui.browseEquipment') : $t('ui.browseProjects') }}
      </router-link>
    </div>
  </div>
</template>
